import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Button, BUTTON_THEME, CheckBox, Input, Select } from '@st-operations/uikit/src/v2'

import { createPartnerFolder, getPartnerFolders, getPartners } from '_services/api-integrations'
import { getTaxonomies } from '_services/taxonomies'
import { getTaxonomyIdSets } from '_services/taxonomy-id-sets'

import { buildSegmentIdOptions } from '../../distribution/segment-id-options'
import {
  ACTIONS,
  MEDIA_TYPE_LABELS,
  NO_TAXONOMY,
  NO_TAXONOMY_SELECTION,
  actionFor,
  actionOf,
} from '../constants'
import Audiences from './audiences'
import ChoiceTiles from './choice-tiles'
import NameChanges from './name-changes'
import NamePreview from './name-preview'

// One page, sections in order, each locked until the one above it is filled.
//
// Not a wizard: the dependencies are real (the partner decides which settings
// exist and whether there are folders, the mapping ID has to come before the
// CSV, the audiences before the names so the preview can show them), but a
// wizard is good for first-time setup and irritating every time after, and
// most use here is running something again with one field changed.
// Two things every Select here needs.
//
// `useReactSelect` - without it Select falls back to its own Dropdown, which
// treats each option as a plain string (option.toLowerCase(), key={option},
// and renders {option} as a child). Handing that {value, label} objects throws
// "Objects are not valid as a React child" the moment the menu opens. The
// {value, label} conversion only exists on the react-select path. Every other
// {value, label} Select in this codebase passes it too.
//
// An unset Select gets '' rather than null. Select renders its value through
// i18n's translate(), which does Object.keys() on anything whose typeof is
// 'object' - and typeof null is 'object', so null throws "Cannot convert
// undefined or null to object" and takes the whole page down. '' is the
// component's own documented default.
// A field applies only when every show_when rule it carries is satisfied by
// the settings currently chosen. TTD's CPM rate, for instance, is only
// relevant on a CPM or Hybrid rate type. The rules come from the partner file
// (see lib/api-integrations/index.coffee) rather than being restated here.
const applies = (definition, settings) => {
  const rules = definition?.show_when
  if (!rules) return true
  return Object.entries(rules).every(([key, allowed]) => allowed.includes(settings?.[key]))
}

// Drop any setting that no longer applies, so a push never carries a rate its
// rate type has no use for - hiding a field but keeping its value would mean
// sending a CPM on a percent-of-media-cost push.
const pruneSettings = (settings, fields) => {
  const kept = {}
  for (const [key, value] of Object.entries(settings || {})) {
    if (!fields[key] || applies(fields[key], settings)) kept[key] = value
  }
  return kept
}

// Start a partner off with whatever defaults its fields declare, so the
// conditional fields have something to react to rather than everything being
// hidden until a rate type is picked.
const defaultSettings = (partner) => {
  const out = {}
  for (const [key, definition] of Object.entries(partner?.settings || {})) {
    if (definition.default !== undefined) out[key] = definition.default
  }
  return pruneSettings(out, partner?.settings || {})
}

// Everything that comes with choosing a partner: its default settings, where
// its segments normally go (TTD's is ROOT), and the action kept unless the new
// partner cannot do it
const partnerPatch = (slug, picked, action) => ({
  partner: slug,
  action: picked?.actions?.includes(action) ? action : 'upsert',
  settings: defaultSettings(picked),
  parent_node: picked?.default_folder || null,
})

// A field's dropdown options. An enum written as an object maps what is saved
// to what is shown - AppNexus's pricing categories are ids named "$1.05" - and
// its keys come back in numeric order rather than as written, so those options
// are put in order by what they show.
const optionsFor = (definition) =>
  Array.isArray(definition.enum)
    ? definition.enum.map((v) => ({ value: v, label: String(v) }))
    : Object.entries(definition.enum)
        .map(([v, l]) => ({ value: v, label: String(l) }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }))

// the option saved, shown by its label rather than by the value behind it
const chosenOption = (definition, value) => {
  if (value === undefined || value === null || value === '') return ''
  return (
    optionsFor(definition).find((o) => String(o.value) === String(value)) || {
      value,
      label: String(value),
    }
  )
}

// what each step is called in the bar across the top
const STEP_TITLES = {
  setup: 'Setup',
  rates: 'Rates',
  folder: 'Parent folder',
  audiences: 'Audiences',
  names: 'Names',
}

// A step of the form. A locked one shows only its title and what it is waiting
// for - not its controls faded out, which read as broken.
const Section = ({ title, hint, locked, waitingFor, hidden, children }) => (
  <div className={`section ${locked ? 'section--locked' : ''}`} hidden={hidden}>
    <div className="section-title">{title}</div>
    <Choose>
      <When condition={locked}>
        <div className="section-hint mb-0">{waitingFor}</div>
      </When>
      <Otherwise>
        <If condition={hint}>
          <div className="section-hint">{hint}</div>
        </If>
        {children}
      </Otherwise>
    </Choose>
  </div>
)

// `chooseTaxonomy`: opened from the sidebar rather than from a taxonomy, so
// the taxonomy is a choice here - and can be none.
const PushForm = ({
  taxonomy,
  chooseTaxonomy,
  push,
  onChange,
  onSave,
  onRun,
  saving,
  error,
  prefill,
}) => {
  const [partners, setPartners] = useState([])
  const [taxonomies, setTaxonomies] = useState([])
  const [folders, setFolders] = useState([])
  const [idSets, setIdSets] = useState([])
  const [newFolder, setNewFolder] = useState('')
  const [newFolderId, setNewFolderId] = useState('')
  // a new folder's rate, for partners whose segments take their folder's
  const [newCpm, setNewCpm] = useState('')
  const [newTypes, setNewTypes] = useState([])
  const [folderError, setFolderError] = useState(null)
  // the step on screen - one at a time, so the page never needs scrolling
  const [current, setCurrent] = useState('setup')
  // The steps reached so far. Run is only on the last step, and the bar only
  // opens steps already reached, so the only way to Run is through every step.
  const [visited, setVisited] = useState(['setup'])

  useEffect(() => {
    getPartners()
      .then((data) => setPartners(data?.items || []))
      .catch(() => setPartners([]))
    getTaxonomyIdSets()
      .then((data) => setIdSets(data?.items || []))
      .catch(() => setIdSets([]))
  }, [])

  useEffect(() => {
    if (!chooseTaxonomy) return
    getTaxonomies({ fields: ['_id', 'name', 'slug'], limit: 1000, sort: 'name,1' })
      .then((data) => setTaxonomies(data?.items || []))
      .catch(() => setTaxonomies([]))
  }, [chooseTaxonomy])

  const partner = useMemo(() => partners.find((p) => p.slug === push.partner) || null, [
    partners,
    push.partner,
  ])

  // folders only exist for some partners, and only matter once one is chosen
  useEffect(() => {
    if (!partner?.has_folders) {
      setFolders([])
      return
    }
    getPartnerFolders(partner.slug)
      .then((data) => setFolders(data?.items || []))
      .catch(() => setFolders([]))
  }, [partner])

  // Yahoo's segments take their folder's rate, so a new folder is given one
  const pricing = partner?.folder_pricing || null
  useEffect(() => {
    setNewCpm('')
    setNewTypes(pricing?.default_media_types || [])
  }, [pricing])

  const set = (patch) => onChange({ ...push, ...patch })

  // Another taxonomy means other audiences - what was chosen belonged to the
  // old one. With none, they are listed by mapping ID.
  const pickTaxonomy = (val) => {
    const picked = taxonomies.find((t) => t._id === val) || null
    if ((picked?._id || null) === (taxonomy?._id || null)) return
    set({
      taxonomy: picked && { _id: picked._id, name: picked.name, slug: picked.slug },
      selection: picked ? { mode: 'all' } : NO_TAXONOMY_SELECTION,
    })
  }

  const taxonomyOptions = [
    { value: NO_TAXONOMY, label: 'No taxonomy - list audiences by mapping ID' },
    ...taxonomies.map((t) => ({ value: t._id, label: t.name })),
  ]

  const toggleType = (type) =>
    setNewTypes((types) =>
      types.includes(type) ? types.filter((t) => t !== type) : [...types, type]
    )

  const addFolder = async () => {
    if (!newFolder) return
    setFolderError(null)
    try {
      const folder = await createPartnerFolder(
        partner.slug,
        newFolder,
        newFolderId || undefined,
        pricing ? { cpm: newCpm, media_types: newTypes } : undefined
      )
      setFolders([...folders, folder])
      set({ parent_node: folder })
      setNewFolder('')
      setNewFolderId('')
      setNewCpm('')
    } catch (e) {
      setFolderError(e?.message || 'could not create that folder')
    }
  }

  const segmentIdOptions = useMemo(() => buildSegmentIdOptions(idSets), [idSets])

  const allFields = partner?.settings || {}
  const settingFields = Object.entries(allFields).filter(([, definition]) =>
    applies(definition, push.settings)
  )

  // changing one setting can hide another, so prune on every change
  const setSetting = (key, value) => {
    const settings = { ...push.settings, [key]: value }
    set({ settings: pruneSettings(settings, allFields) })
  }

  const hasPartner = Boolean(push.partner)
  const hasMappingId = Boolean(push.mapping_id)
  const ready = hasPartner && hasMappingId

  // A list of audiences with nothing in it - the CSV or paste option picked
  // and nothing matched yet. Saving that is fine; running it would send nothing.
  const noAudiences = push.selection?.mode === 'ids' && !(push.selection?.audience_ids || []).length

  // What the action needs: pricing unless it only renames, names unless it only
  // sets rates, and a folder only when segments may be created
  const action = actionOf(push).value
  // A push opened from a taxonomy's coverage arrives with its partner, mapping
  // ID and audiences chosen - applied once the partners are in, the same way
  // as choosing the partner here, so its defaults come with it. Once only: it
  // is a starting point, and everything stays editable.
  const prefilled = useRef(false)
  useEffect(() => {
    if (!prefill || prefilled.current || !partners.length) return
    prefilled.current = true
    const picked = partners.find((p) => p.slug === prefill.partner && p.available)
    set({
      ...(picked ? partnerPatch(picked.slug, picked, action) : {}),
      mapping_id: prefill.mapping_id || push.mapping_id,
      selection: prefill.selection || push.selection,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partners, prefill])

  // in the partner's words - Yahoo's upsert sets no rate
  const offered = ACTIONS.filter(
    (a) => !partner?.actions || partner.actions.includes(a.value)
  ).map((a) => actionFor(a, partner))
  const usesRates = action !== 'rename'
  const usesNames = action !== 'set_rate'
  const usesFolder = action === 'upsert' && partner?.has_folders
  // only once there is a partner with something to set
  const showsRates = usesRates && settingFields.length > 0
  // new segments need somewhere to go - TTD starts on ROOT, Adelphic on nothing
  const noFolder = Boolean(usesFolder && !push.parent_node?.id)

  // steps are numbered in the order shown, whichever are shown
  const steps = [
    'setup',
    showsRates && 'rates',
    usesFolder && 'folder',
    'audiences',
    usesNames && 'names',
  ].filter(Boolean)
  const step = (key) => steps.indexOf(key) + 1

  // A step can disappear - changing the action takes the names away - so fall
  // back to the first. Everything after setup waits for a partner and mapping ID.
  const at = steps.includes(current) ? current : steps[0]
  const position = steps.indexOf(at)
  const nextStep = steps[position + 1]
  const previousStep = steps[position - 1]

  const goTo = (key) => {
    setCurrent(key)
    setVisited((seen) => (seen.includes(key) ? seen : [...seen, key]))
  }

  // a different taxonomy, partner, mapping ID or action means different steps
  // or audiences - so they all have to be gone through again
  useEffect(() => {
    setVisited(['setup'])
  }, [taxonomy?._id, push.partner, push.mapping_id, action])

  // what a locked step is waiting for
  const waitingFor = hasPartner ? 'Choose a mapping ID first.' : 'Choose a partner first.'

  // the push in one line, beside Run - and why Run is greyed out, when it is
  const chosen = push.selection?.audience_ids?.length || 0
  const summary = [
    actionOf(push, partner).label,
    partner?.name,
    push.mapping_id && `mapping ID ${push.mapping_id}`,
    push.selection?.mode === 'ids'
      ? `${chosen} audience${chosen === 1 ? '' : 's'}`
      : 'every audience',
  ].filter(Boolean)
  let blocker = null
  if (!hasPartner) blocker = 'Choose a partner to run this push.'
  else if (!hasMappingId) blocker = 'Choose a mapping ID to run this push.'
  else if (noFolder) blocker = 'Choose a parent folder to run this push.'
  else if (noAudiences) blocker = 'Choose at least one audience to run this push.'
  else if (nextStep) blocker = `Step ${position + 1} of ${steps.length} - Run is on the last step.`

  // A folder made on the form that the partner does not have yet (Yahoo makes
  // it with its first segments) - kept in the list, so it can be picked again
  const folderLabel = (folder) =>
    folder.pending ? `${folder.name} (new - made with this push)` : folder.name
  const pendingFolder = push.parent_node?.pending ? push.parent_node : null
  const folderOptions =
    pendingFolder && !folders.some((f) => f.id === pendingFolder.id)
      ? [...folders, pendingFolder]
      : folders
  const priced = (folder) =>
    (folder?.fees || []).map((f) => MEDIA_TYPE_LABELS[f.mediaType] || f.mediaType).join(', ')

  return (
    <div className="push-form">
      <If condition={partner?.mock}>
        <div className="mock-banner">
          <strong>{partner.name} is still mocked.</strong> Running this will not contact the partner
          — it writes results as if it had, and marks the push as mocked so nobody mistakes it for a
          real one.
        </div>
      </If>

      {/* where you are, and a way to jump to any step that is ready */}
      <ol className="stepper">
        <For each="key" of={steps} index="i">
          <li key={key}>
            <button
              type="button"
              className={`stepper__step ${key === at ? 'is-current' : ''} ${
                i < position ? 'is-done' : ''
              }`}
              disabled={key !== at && !visited.includes(key)}
              onClick={() => goTo(key)}
            >
              <span className="stepper__number">{i + 1}</span>
              <span className="stepper__title">{STEP_TITLES[key]}</span>
            </button>
          </li>
        </For>
      </ol>

      {/* the three short choices that decide everything else, together */}
      <Section title={`${step('setup')}. Setup`} hidden={at !== 'setup'}>
        <If condition={chooseTaxonomy}>
          <div className="taxonomy-choice">
            <Select
              useReactSelect
              isSearchable
              name="taxonomy"
              label="Taxonomy"
              value={taxonomy ? { value: taxonomy._id, label: taxonomy.name } : taxonomyOptions[0]}
              options={taxonomyOptions}
              onChange={(option, name, val) => pickTaxonomy(val)}
            />
            <div className="field-hint">
              {taxonomy
                ? 'Its audiences can all be sent, picked from a list, or listed by mapping ID.'
                : 'Audiences are listed by mapping ID - uploaded or pasted - and can be from any taxonomy.'}
            </div>
          </div>
        </If>
        <div className="setup-grid">
          <Select
            useReactSelect
            isSearchable
            name="partner"
            label="Partner"
            value={
              push.partner ? { value: push.partner, label: partner?.name || push.partner } : ''
            }
            options={partners.map((p) => ({
              value: p.slug,
              label: p.available ? p.name : `${p.name} (not built yet)`,
              disabled: !p.available,
            }))}
            onChange={(option, name, val) =>
              set(
                partnerPatch(
                  val,
                  partners.find((p) => p.slug === val),
                  action
                )
              )
            }
          />
          <div>
            <Select
              useReactSelect
              isSearchable
              name="mapping_id"
              label="Mapping ID"
              value={
                push.mapping_id
                  ? {
                      value: push.mapping_id,
                      label:
                        segmentIdOptions.find((o) => o.value === push.mapping_id)?.label ||
                        push.mapping_id,
                    }
                  : ''
              }
              options={segmentIdOptions}
              onChange={(option, name, val) => set({ mapping_id: val })}
            />
            <div className="field-hint">
              The ID the partner gets, from audience.ids - the same list distributions use.
            </div>
          </div>
        </div>

        <div className="field-label">Action</div>
        <ChoiceTiles
          name="action"
          value={action}
          options={offered}
          onChange={(value) => set({ action: value })}
        />
      </Section>

      <If condition={showsRates}>
        <Section
          title={`${step('rates')}. Rates`}
          hint={`What ${partner.name} charges for these segments.`}
          hidden={at !== 'rates'}
        >
          <div className="fields-grid">
            <For each="entry" of={settingFields} index="i">
              <div key={entry[0]}>
                <Choose>
                  <When condition={entry[1].enum}>
                    <Select
                      useReactSelect
                      name={entry[0]}
                      label={entry[1].label || entry[0]}
                      value={chosenOption(entry[1], push.settings?.[entry[0]])}
                      options={optionsFor(entry[1])}
                      onChange={(option, name, val) => setSetting(entry[0], val)}
                    />
                  </When>
                  <Otherwise>
                    <Input
                      name={entry[0]}
                      label={entry[1].label || entry[0]}
                      value={String(push.settings?.[entry[0]] ?? '')}
                      onChange={(val) => setSetting(entry[0], val)}
                    />
                  </Otherwise>
                </Choose>
              </div>
            </For>
          </div>
        </Section>
      </If>

      <If condition={usesFolder}>
        <Section
          title={`${step('folder')}. Parent folder`}
          hint={
            partner?.folder_hint || "Where new segments go in the partner's system. Top level only."
          }
          hidden={at !== 'folder'}
        >
          <Select
            useReactSelect
            name="parent_node"
            value={
              push.parent_node
                ? {
                    value: push.parent_node.id,
                    // the name the partner gives it, once asked - a default
                    // folder may only know its id until then (Adelphic's 537408)
                    label: folderLabel(
                      folderOptions.find((f) => f.id === push.parent_node.id) || push.parent_node
                    ),
                  }
                : ''
            }
            options={folderOptions.map((f) => ({ value: f.id, label: folderLabel(f) }))}
            onChange={(option, name, val) =>
              set({ parent_node: folderOptions.find((f) => f.id === val) || null })
            }
          />
          <If condition={pendingFolder}>
            <div className="field-hint">
              Made in {partner?.name} along with the first segments put in it, at $
              {pendingFolder.fees?.[0]?.fee} CPM ({priced(pendingFolder)}).
            </div>
          </If>

          <If condition={partner?.can_create_folder}>
            <div className="new-folder">
              <div className="flex gap-2 items-end flex-wrap">
                <Input
                  name="new_folder"
                  label="Or make a new one"
                  value={newFolder}
                  onChange={(val) => setNewFolder(val)}
                />
                <If condition={!partner?.chooses_folder_ids}>
                  <Input
                    name="new_folder_id"
                    label="Its ID (optional)"
                    description="Left blank, made from the name"
                    value={newFolderId}
                    onChange={(val) => setNewFolderId(val)}
                  />
                </If>
                <If condition={pricing}>
                  <Input
                    name="new_folder_cpm"
                    label="CPM ($)"
                    value={newCpm}
                    onChange={(val) => setNewCpm(val)}
                  />
                </If>
                <Button
                  theme={BUTTON_THEME.SECONDARY}
                  onClick={addFolder}
                  disabled={!newFolder || Boolean(pricing && (!newCpm || !newTypes.length))}
                >
                  Create
                </Button>
              </div>
              <If condition={pricing}>
                <div className="field-label mt-3">Priced for</div>
                <div className="media-types">
                  <For each="type" of={pricing.media_types || []}>
                    <label key={type} className="media-type">
                      <CheckBox
                        checked={newTypes.includes(type)}
                        onChange={() => toggleType(type)}
                      />
                      <span>{MEDIA_TYPE_LABELS[type] || type}</span>
                    </label>
                  </For>
                </div>
                <div className="field-hint">
                  Segments in this folder take its rate - up to ${pricing.max_cpm} CPM.
                </div>
              </If>
              <If condition={folderError}>
                <div className="text-red-600 text-sm mt-2">{folderError}</div>
              </If>
            </div>
          </If>
        </Section>
      </If>

      <Section
        title={`${step('audiences')}. Audiences`}
        locked={!ready}
        waitingFor={waitingFor}
        hidden={at !== 'audiences'}
      >
        {/* started afresh for another taxonomy, so nothing from the old one stays */}
        <Audiences
          key={taxonomy?._id || NO_TAXONOMY}
          taxonomy={taxonomy}
          mappingId={push.mapping_id}
          value={push.selection}
          onChange={(selection) => set({ selection })}
        />
      </Section>

      <If condition={usesNames}>
        <Section
          title={`${step('names')}. Names`}
          hint="Change how audience names read before they reach the partner."
          locked={!ready}
          waitingFor={waitingFor}
          hidden={at !== 'names'}
        >
          <NameChanges
            value={push.name_changes}
            onChange={(name_changes) => set({ name_changes })}
          />
          <NamePreview
            taxonomyId={taxonomy?._id}
            mappingId={push.mapping_id}
            selection={push.selection}
            nameChanges={push.name_changes}
          />
        </Section>
      </If>

      {/* what Run will do, and why it cannot yet - Run is on the last step */}
      <div className="form-footer">
        <div>
          <div className="text-sm font-medium">{summary.join(' · ')}</div>
          <If condition={blocker}>
            <div className="text-xs text-gray-500 mt-1">{blocker}</div>
          </If>
          <If condition={error}>
            <div className="text-xs text-red-600 mt-1">{error}</div>
          </If>
        </div>
        <div className="flex gap-2">
          <If condition={previousStep}>
            <Button theme={BUTTON_THEME.GHOST} onClick={() => goTo(previousStep)}>
              Back
            </Button>
          </If>
          <Button theme={BUTTON_THEME.SECONDARY} onClick={onSave} disabled={!ready || saving}>
            Save draft
          </Button>
          <Choose>
            <When condition={nextStep}>
              <Button
                theme={BUTTON_THEME.PRIMARY}
                onClick={() => goTo(nextStep)}
                disabled={!ready || (at === 'folder' && noFolder)}
              >
                Continue
              </Button>
            </When>
            <Otherwise>
              <Button
                theme={BUTTON_THEME.PRIMARY}
                onClick={onRun}
                disabled={!ready || saving || noAudiences || noFolder}
              >
                Run
              </Button>
            </Otherwise>
          </Choose>
        </div>
      </div>
    </div>
  )
}

export default PushForm
